/**
 * SoleadLab 2.0 — IV Session Detail (Right Panel)
 */

import { useState, useEffect } from 'react';
import { useIVStore } from '@/stores/ivStore';
import IVImportWizard from './IVImportWizard';
import IVMeasurementsTable from './IVMeasurementsTable';
import IVAssignPanel from './IVAssignPanel';
import IVAnalysisPanel from './IVAnalysisPanel';
import IVReportPreview from './IVReportPreview';

type TabId = 'import' | 'measurements' | 'assign' | 'analysis' | 'report';

const TABS: Array<{ id: TabId; label: string }> = [
  { id: 'import', label: 'Import' },
  { id: 'measurements', label: 'Measurements' },
  { id: 'assign', label: 'Assign' },
  { id: 'analysis', label: 'Analysis' },
  { id: 'report', label: 'Report' },
];

export default function IVSessionDetail() {
  const sessions = useIVStore((s) => s.sessions);
  const selectedSessionId = useIVStore((s) => s.selectedSessionId);
  const pendingTab = useIVStore((s) => s.pendingTab);
  const setPendingTab = useIVStore((s) => s.setPendingTab);
  const [activeTab, setActiveTab] = useState<TabId>('import');

  const session = sessions.find((s) => s.id === selectedSessionId);

  // Reset to 'import' tab when switching sessions
  useEffect(() => {
    setActiveTab('import');
  }, [selectedSessionId]);

  // Auto-switch tab when requested (e.g. after auto-import on session creation)
  useEffect(() => {
    if (pendingTab) {
      setActiveTab(pendingTab);
      setPendingTab(null);
    }
  }, [pendingTab, setPendingTab]);

  if (!session) {
    return (
      <div className="iv-detail">
        <div className="iv-detail__empty">Select a session to view details</div>
      </div>
    );
  }

  return (
    <div className="iv-detail">
      <div className="iv-detail__header">
        <div className="iv-detail__header-row">
          <span className="iv-detail__name">{session.name}</span>
          <span className={`iv-badge iv-badge--${session.status}`}>{session.status}</span>
        </div>
        <div className="iv-detail__meta">
          <span>Scope: {session.measurement_scope}</span>
          {session.site_name && <span>Site: {session.site_name}</span>}
          {session.inverter_id && <span>Inverter: {session.inverter_id}</span>}
          {session.nominal_pmax != null && <span>Pmax: {session.nominal_pmax} W</span>}
        </div>
        {session.file_metadata && Object.keys(session.file_metadata).length > 0 && (
          <div className="iv-detail__file-meta">
            {Object.entries(session.file_metadata).map(([k, v]) => (
              <span key={k} className="iv-detail__file-meta-item">
                <span className="iv-detail__file-meta-key">{k.replace(/_/g, ' ')}</span>
                <span className="iv-detail__file-meta-val">{String(v)}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="iv-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`iv-tab${activeTab === tab.id ? ' iv-tab--active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="iv-tab-content">
        {activeTab === 'import' && <IVImportWizard sessionId={session.id} />}
        {activeTab === 'measurements' && <IVMeasurementsTable />}
        {activeTab === 'assign' && <IVAssignPanel sessionId={session.id} />}
        {activeTab === 'analysis' && <IVAnalysisPanel sessionId={session.id} />}
        {activeTab === 'report' && <IVReportPreview sessionId={session.id} />}
      </div>
    </div>
  );
}
